import bootstrapPlugin from '@fullcalendar/bootstrap';
import daLocale from '@fullcalendar/core/locales/da';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  DialogActions,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemText,
  Popover,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';
import './styles.css';

import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import HtmlTooltip from '@/components/tooltip/HtmlTooltip';

const EventCalendar = ({
  subscriptions,
  buildings, // Updated: Using buildings prop to access building data
  events,
  updateSubscription,
  manualRunRequest,
  selectedSubscriptions,
  currentViewDate,
  handleSupportClick,
  onDateSelect,
}) => {
  const calendarRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null); // State for Add/Edit Popover
  const [deletePopoverEl, setDeletePopoverEl] = useState(null); // State for Delete Popover
  const [pseudoEvent, setPseudoEvent] = useState(null); // State for Pseudo Event
  const [isEventClick, setIsEventClick] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState({
    id: null,
    title: '',
    start: new Date().toISOString(),
    end: new Date().toISOString(),
    type: 'occupied',
  });
  const [selectedEventSubscriptions, setSelectedEventSubscriptions] = useState(
    {},
  );
  const [allSelected, setAllSelected] = useState(false);

  // Helper function to check the selection state of a building's subscriptions
  const checkBuildingSelectionState = (building) => {
    const buildingSubs = subscriptions.filter(
      (sub) =>
        building.JobSubscriptions.includes(sub.JobSubscriptionId) &&
        sub.StateCode === 'Active',
    ); // Only active subscriptions
    const selectedSubs = buildingSubs.filter(
      (sub) => selectedEventSubscriptions[sub.JobSubscriptionId],
    );

    return {
      allSelected: selectedSubs.length === buildingSubs.length, // All active subscriptions are selected
      someSelected:
        selectedSubs.length > 0 && selectedSubs.length < buildingSubs.length, // Some active subscriptions are selected
    };
  };
  const handleBuildingSelectAllToggle = (buildingId, checked) => {
    const newSelectedSubscriptions = { ...selectedEventSubscriptions };

    // Get only active subscriptions for the building
    const buildingSubscriptions = subscriptions.filter(
      (sub) =>
        buildings
          .find((building) => building.BuildingId === buildingId)
          .JobSubscriptions.includes(sub.JobSubscriptionId) &&
        sub.StateCode === 'Active', // Only consider active subscriptions
    );

    // Select/deselect only active subscriptions
    buildingSubscriptions.forEach((sub) => {
      newSelectedSubscriptions[sub.JobSubscriptionId] = checked;
    });

    setSelectedEventSubscriptions(newSelectedSubscriptions);
  };

  // Function to handle individual subscription toggling
  const handleSubscriptionToggle = (subscriptionId) => {
    setSelectedEventSubscriptions((prev) => ({
      ...prev,
      [subscriptionId]: !prev[subscriptionId],
    }));
  };

  // Global "Select All" functionality
  const handleSelectAllToggle = () => {
    const newSelectedSubscriptions = {};
    if (allSelected) {
      subscriptions.forEach((sub) => {
        newSelectedSubscriptions[sub.JobSubscriptionId] = false;
      });
    } else {
      subscriptions.forEach((sub) => {
        newSelectedSubscriptions[sub.JobSubscriptionId] = true;
      });
    }

    setSelectedEventSubscriptions(newSelectedSubscriptions);
    setAllSelected(!allSelected); // Toggle select all state
  };

  useEffect(() => {
    if (calendarRef.current && currentViewDate) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(currentViewDate.toISO());
    }
  }, [currentViewDate]);

  const handleDateSelect = (selectInfo) => {
    setIsEventClick(false);
    const calendarApi = calendarRef.current.getApi();

    if (pseudoEvent) {
      const existingEvent = calendarApi.getEventById('pseudo-event');
      if (existingEvent) {
        existingEvent.remove();
      }
      setPseudoEvent(null);
    }

    // Create a temporary pseudo event
    const tempEvent = {
      id: 'pseudo-event',
      title: '',
      start: selectInfo.startStr,
      end: selectInfo.endStr,
      allDay: selectInfo.allDay,
    };

    calendarApi.addEvent(tempEvent); // Add the pseudo event to the calendar

    setSelectedEvent({
      id: null,
      title: '',
      start: selectInfo.startStr,
      end: selectInfo.endStr,
      type: 'occupied',
    });

    setPseudoEvent(tempEvent); // Store pseudo-event for future removal

    // Initialize selected subscriptions state for the event
    setSelectedEventSubscriptions(selectedSubscriptions);

    // Find the DOM element of the new pseudo event
    const eventEl = document.querySelector(`[data-event-id="pseudo-event"]`);
    if (eventEl) {
      setAnchorEl(eventEl); // Anchor the popover to the pseudo event element
    }
  };

  const handleTimeChange = (field, value) => {
    setSelectedEvent((prev) => ({
      ...prev,
      [field]: value, // Update the field directly without immediate validation
    }));
  };

  const validateAndSetEventTimes = () => {
    setSelectedEvent((prev) => {
      let newStart = DateTime.fromISO(prev.start);
      let newEnd = DateTime.fromISO(prev.end);

      if (newStart > newEnd) {
        newEnd = newStart.plus({ minutes: 15 }); // Ensure start is before end
      }

      return {
        ...prev,
        start: newStart.toISO(),
        end: newEnd.toISO(),
      };
    });
  };

  const handleRemoveEvent = async () => {
    const { id } = selectedEvent;
    if (!id) return; // No event selected, do nothing

    const [subscriptionId, calendarType, eventIndex] = id.split('-'); // Extract id components
    const calendarKey =
      calendarType === 'empty'
        ? 'BuildingEmptyCalendar'
        : 'BuildingOccupiedCalendar';

    const subscription = subscriptions.find(
      (sub) => sub.JobSubscriptionId.toString() === subscriptionId,
    );

    if (!subscription) {
      console.error('Subscription not found for id:', subscriptionId);
      return;
    }

    // Ensure the eventIndex is a valid number
    const index = parseInt(eventIndex, 10);
    if (
      isNaN(index) ||
      index < 0 ||
      index >= subscription.UserInput[calendarKey].length
    ) {
      console.error('Invalid event index:', eventIndex);
      return;
    }

    // Get the event to be removed
    const eventToRemove = subscription.UserInput[calendarKey][index];
    const eventStart = DateTime.fromISO(eventToRemove.From);
    const now = DateTime.now();
    let requestManualRun = false;

    // Check if the event falls within the current day
    if (eventStart.hasSame(now, 'day')) {
      requestManualRun = true; // Flag to trigger manual run
    }

    // Remove the event at the specified index
    const updatedEvents = [...subscription.UserInput[calendarKey]]; // Copy the existing events
    updatedEvents.splice(index, 1); // Remove the event at the specified index

    // Create an updated version of the UserInput
    const updatedUserInput = {
      ...subscription.UserInput,
      [calendarKey]: updatedEvents, // Use the updated event list
    };

    // Prepare the payload to be sent to the API
    const updatedData = {
      DataInput: subscription.DataInput,
      UserInput: updatedUserInput,
      AlgorithmInput: subscription.AlgorithmInput,
      DisplayName: subscription.DisplayName,
    };

    // Trigger the update function in CalendarPage
    await updateSubscription({ subscriptionId, data: updatedData });

    // If the event was for today, request a manual run
    if (requestManualRun) {
      manualRunRequest({ subscriptionId }); // Trigger a manual run for the subscription
    }

    handleDeletePopoverClose(); // Close popover after removal action
  };

  const handleExistingEventClick = (clickInfo) => {
    if (clickInfo.event.id === 'pseudo-event') {
      // If pseudo-event, handle it separately
      handlePseudoEventClick(clickInfo); // Ensure to call the pseudo-event handler
      return; // Prevent the rest of the function from executing
    }

    setIsEventClick(true);
    const event = clickInfo.event;
    setSelectedEvent({
      id: event.id,
      title: event.title,
      start: event.start.toISOString(),
      end: event.end.toISOString(),
      type: event.extendedProps.type,
      subscriptionDisplayName: event.extendedProps.subscriptionDisplayName,
      buildingName: event.extendedProps.buildingName,
      eventName: event.extendedProps.eventName,
      displayName: event.extendedProps.subscriptionDisplayName,
    });
    setDeletePopoverEl(clickInfo.el); // Set state to open the delete popover
  };

  const handlePseudoEventClick = (clickInfo) => {
    setAnchorEl(clickInfo.el); // Set anchorEl for the popover to open for creation
  };

  const handlePopoverClose = () => {
    setAnchorEl(null); // Close the add/edit popover

    if (pseudoEvent) {
      const calendarApi = calendarRef.current.getApi();
      const tempEvent = calendarApi.getEventById('pseudo-event');
      if (tempEvent) {
        tempEvent.remove(); // Remove the pseudo-event
      }
      setPseudoEvent(null); // Clear pseudo event state
    }

    setSelectedEvent({
      id: null,
      title: '',
      start: new Date().toISOString(),
      end: new Date().toISOString(),
      type: 'occupied',
    });
  };

  const handleDeletePopoverClose = () => {
    setDeletePopoverEl(null); // Close the delete popover
  };

  const handleTypeChange = (event) => {
    setSelectedEvent((prev) => ({
      ...prev,
      type: event.target.value,
    }));
  };

  const doesOverlap = (newEvent, existingEvents) => {
    return existingEvents.some(
      (event) => newEvent.start < event.To && newEvent.end > event.From,
    );
  };

  const handleAddEvent = async () => {
    const startDate = DateTime.fromISO(selectedEvent.start);
    const now = DateTime.now();
    let requestManualRun = false;
    // const endDate = DateTime.fromISO(selectedEvent.end);

    // Prevent adding events in the past
    if (startDate < DateTime.now()) {
      alert('Cannot add events in the past.');
      return;
    }

    // Check if the event starts today or tomorrow
    if (startDate.hasSame(now, 'day')) {
      requestManualRun = true; // Set the flag to request a manual run
      // Perform the additional logic you need for events happening today
      // (e.g., triggering additional API calls, updating UI, etc.)
    } else if (startDate.hasSame(now.plus({ days: 1 }), 'day')) {
    }

    // Gather selected subscriptions and their existing events
    const selectedSubs = Object.entries(selectedEventSubscriptions)
      .filter(
        ([id, isSelected]) => typeof isSelected === 'boolean' && isSelected,
      ) // Ensure the value is a boolean 'true'
      .map(([id]) => id); // Extract only subscription IDs

    if (selectedSubs.length === 0) {
      alert('Please select at least one subscription.');
      return;
    }

    for (let subId of selectedSubs) {
      const sub = subscriptions.find(
        (s) => s.JobSubscriptionId.toString() === subId.toString(),
      );

      const calendarType =
        selectedEvent.type === 'occupied'
          ? 'BuildingOccupiedCalendar'
          : 'BuildingEmptyCalendar';
      const oppositeCalendarType =
        selectedEvent.type === 'occupied'
          ? 'BuildingEmptyCalendar'
          : 'BuildingOccupiedCalendar';

      // Ensure no overlap within the selected type
      if (doesOverlap(selectedEvent, sub.UserInput[calendarType])) {
        alert(
          `Event overlaps with an existing event for subscription: ${sub.DisplayName}`,
        );
        return;
      }

      // Ensure mutual exclusivity between occupied and empty calendars
      if (doesOverlap(selectedEvent, sub.UserInput[oppositeCalendarType])) {
        alert(
          `Cannot add event to ${calendarType} because it overlaps with an event in ${oppositeCalendarType} for subscription: ${sub.DisplayName}`,
        );
        return;
      }
    }

    // If validations pass, add the event
    for (let subscriptionId of selectedSubs) {
      const sub = subscriptions.find(
        (s) => s.JobSubscriptionId.toString() === subscriptionId,
      );
      const calendarType =
        selectedEvent.type === 'occupied'
          ? 'BuildingOccupiedCalendar'
          : 'BuildingEmptyCalendar';

      const newEvent = {
        From: selectedEvent.start,
        To: selectedEvent.end,
        Name: selectedEvent.title || null,
      };

      // Create a new copy of UserInput and the relevant calendar array
      const updatedUserInput = {
        ...sub.UserInput,
        [calendarType]: [...sub.UserInput[calendarType], newEvent], // Use a new array copy
      };

      // Prepare the updated data to be sent to the API
      const updatedData = {
        DataInput: sub.DataInput,
        UserInput: updatedUserInput,
        AlgorithmInput: sub.AlgorithmInput,
        DisplayName: sub.DisplayName,
      };

      // Update subscription via API
      await updateSubscription({ subscriptionId, data: updatedData }); // Trigger the update function in CalendarPage
      if (requestManualRun) {
        manualRunRequest({ subscriptionId }); // Request a manual run for the subscription
      }
    }

    // Clear pseudo-event state
    setPseudoEvent(null);
    handlePopoverClose();
  };

  const handleTodayButtonClick = () => {
    onDateSelect(DateTime.now()); // Use the onDateSelect prop to update the date
  };

  return (
    <>
      <div className="fc-light-theme">
        <FullCalendar
          ref={calendarRef}
          plugins={[
            dayGridPlugin,
            timeGridPlugin,
            interactionPlugin,
            listPlugin,
            bootstrapPlugin,
          ]}
          initialView="timeGridWeek"
          themeSystem="standard"
          locales={[daLocale]}
          nowIndicator={true}
          firstDay={1}
          locale="da"
          height="auto"
          selectable={true}
          select={handleDateSelect}
          slotEventOverlap={false} // Prevent overlapping of events
          events={events}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            start: 'prev,next',
            end: 'dayGridMonth,timeGridWeek,timeGridDay',
          }}
          titleFormat={{
            month: 'long', // This will show only the full month name
          }}
          customButtons={{
            today: {
              text: 'I Dag',
              click: handleTodayButtonClick,
            },
          }}
          // height="75vh"
          eventClick={handleExistingEventClick} // Use handleExistingEventClick for existing event clicks
          // Adjust the time display format to 24-hour format
          slotLabelFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false, // Disable AM/PM, use 24-hour format
          }}
          eventTimeFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false, // Disable AM/PM, use 24-hour format
          }}
          views={{
            dayGridMonth: {
              dayHeaderFormat: { weekday: 'long' }, // Only weekday names for month view
            },
            timeGridWeek: {
              dayHeaderContent: (args) => {
                const date = args.date;
                const isToday = DateTime.now().hasSame(
                  DateTime.fromJSDate(date),
                  'day',
                ); // Check if this is today
                return (
                  <div style={{ textAlign: 'center', marginBottom: 8 }}>
                    <Avatar
                      variant="rounded" // Makes the Avatar have rounded corners
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '0.8rem', // Adjust the font size if needed
                        color: isToday ? 'white' : 'inherit', // White text for today
                        backgroundColor: isToday
                          ? 'hsl(63, 100%, 40%)'
                          : 'inherit', // Highlight today in greenish hue
                        // width: 36, // Adjust the size of the Avatar if needed
                        // height: 36, // Adjust the size of the Avatar if needed
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 2,
                      }}
                    >
                      {DateTime.fromJSDate(date).toFormat('dd')}{' '}
                      {/* Danish locale for weekday */}
                    </Avatar>
                    <div>
                      {DateTime.fromJSDate(date)
                        .setLocale('da')
                        .toFormat('ccc')}
                    </div>
                  </div>
                );
              },
            },
            timeGridDay: {
              dayHeaderFormat: {
                weekday: 'long', // Full weekday name
                month: 'long', // Full month name
                day: 'numeric', // Day of the month
              },
            },
          }}
          allDaySlot={false}
        />
      </div>

      {/* Popover for Adding or Editing Events */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? 'hsl(0, 0%, 100%)' : '#121212',
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {selectedEvent.displayName || 'Ny begivenhed'}
          </Typography>
          <IconButton
            onClick={handlePopoverClose}
            sx={{
              borderRadius: 1,
              ml: 1,
              ':hover': {
                borderColor: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'hsl(0, 0%, 80%)'
                    : 'hsl(0, 0%, 30%)',
                cursor: 'pointer',
              },
            }}
          >
            <CloseRoundedIcon
              sx={{
                ':hover': { opacity: '100%', transition: 'opacity 0.3s' },
              }}
              opacity="20%"
            />
          </IconButton>
        </Box>
        <Divider fullWidth />
        <Box
          sx={{
            p: 2,
            width: '500px',
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? 'hsl(0, 0%, 100%)' : '#121212',
            mt: -2,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                margin="dense"
                fullWidth
                variant="outlined"
                label="Navn"
                type="text"
                sx={{ flex: 1 }} // Adjust flex to allow space for the button
                value={selectedEvent.title}
                required
                onChange={(e) =>
                  setSelectedEvent({ ...selectedEvent, title: e.target.value })
                }
              />
            </Grid>
            {/* <Grid item xs={6}>
              <TextField
                label="Start Tidspunkt"
                type="datetime-local"
                fullWidth
                value={DateTime.fromISO(selectedEvent.start).toFormat('yyyy-MM-dd\'T\'HH:mm')}
                onChange={(e) => handleTimeChange('start', e.target.value)}
                InputProps={{ step: 900 }} // 15-minute step
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Slut Tidspunkt"
                type="datetime-local"
                fullWidth
                value={DateTime.fromISO(selectedEvent.end).toFormat('yyyy-MM-dd\'T\'HH:mm')}
                onChange={(e) => handleTimeChange('end', e.target.value)}
                InputProps={{ step: 900 }} // 15-minute step
              />
            </Grid> */}
            <Grid item xs={6}>
              <TextField
                label="Start Tidspunkt"
                type="datetime-local"
                fullWidth
                value={DateTime.fromISO(selectedEvent.start).toFormat(
                  "yyyy-MM-dd'T'HH:mm",
                )}
                onChange={(e) => handleTimeChange('start', e.target.value)} // Update the value directly
                onBlur={validateAndSetEventTimes} // Validate after losing focus
                InputProps={{ step: 900 }} // 15-minute step
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Slut Tidspunkt"
                type="datetime-local"
                fullWidth
                value={DateTime.fromISO(selectedEvent.end).toFormat(
                  "yyyy-MM-dd'T'HH:mm",
                )}
                onChange={(e) => handleTimeChange('end', e.target.value)} // Update the value directly
                onBlur={validateAndSetEventTimes} // Validate after losing focus
                InputProps={{ step: 900 }} // 15-minute step
              />
            </Grid>
          </Grid>
          <FormControl component="fieldset" sx={{ mt: 2 }}>
            <RadioGroup
              row
              value={selectedEvent.type}
              onChange={handleTypeChange}
            >
              <FormControlLabel
                value="occupied"
                control={<Radio />}
                label="Bygning i brug"
              />
              <FormControlLabel
                value="empty"
                control={<Radio />}
                label="Bygning ikke i brug"
              />
            </RadioGroup>
          </FormControl>

          <Divider sx={{ my: 2 }} />

          {/* Global select/deselect all */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography
                sx={{
                  ':hover': { cursor: 'pointer', opacity: '1' },
                  transition: 'opacity 0.3s',
                }}
                component="a"
                onClick={handleSelectAllToggle}
              >
                {allSelected
                  ? 'Fravælg alle blandesløjer'
                  : 'Vælg alle blandesløjfer'}
              </Typography>
              <HtmlTooltip
                title="Vi har forsøgt at lave navngivningen af de enkelte blandesløjfer gennemskuelig. Skulle I have ønsker til at få ændret navnet, så det er nemmere at lave begivenheder, som kun gælder dele af bygningen, så ræk ud til vores support"
                content={
                  <Box>
                    <IconButton
                      sx={{ borderRadius: 1, mr: 1 }}
                      onClick={handleSupportClick}
                    >
                      <SupportAgentRoundedIcon fontSize="small" />
                    </IconButton>
                    (tryk på ikonet for at gå til support siden)
                  </Box>
                }
              >
                <InfoRoundedIcon
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'light' ? 'lightgray' : 'gray',
                    ml: 1,
                  }}
                  fontSize="small"
                />
              </HtmlTooltip>
            </Box>
            {/* <Typography
              sx={{
                opacity: '0.7',
                fontSize: '0.9rem',
                ':hover': { cursor: 'pointer', opacity: '1' },
                transition: 'opacity 0.3s',
              }}
              component="a"
              onClick={handleSelectAllToggle}
            >
              {allSelected ? "Fravælg alle" : "Vælg alle"}
            </Typography> */}
          </Box>

          {/* Group subscriptions by building */}
          {buildings.map((building) => {
            const { allSelected, someSelected } =
              checkBuildingSelectionState(building);

            return (
              <Box key={building.BuildingId} sx={{ mb: 2 }}>
                {/* Building-level select/deselect all */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={(e) =>
                        handleBuildingSelectAllToggle(
                          building.BuildingId,
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label={building.Name}
                  sx={{ fontWeight: 'bold' }}
                />

                {/* Subscriptions under each building */}
                <Box sx={{ ml: 3 }}>
                  {subscriptions
                    .filter((sub) =>
                      building.JobSubscriptions.includes(sub.JobSubscriptionId),
                    )
                    .map((sub) => {
                      const calendarType =
                        selectedEvent.type === 'occupied'
                          ? 'BuildingOccupiedCalendar'
                          : 'BuildingEmptyCalendar';
                      const oppositeCalendarType =
                        selectedEvent.type === 'occupied'
                          ? 'BuildingEmptyCalendar'
                          : 'BuildingOccupiedCalendar';
                      const isConflicting =
                        doesOverlap(
                          selectedEvent,
                          sub.UserInput[calendarType],
                        ) ||
                        doesOverlap(
                          selectedEvent,
                          sub.UserInput[oppositeCalendarType],
                        );
                      const isActive = sub.StateCode === 'Active'; // Check if the subscription is active

                      return (
                        <FormControlLabel
                          key={sub.JobSubscriptionId}
                          control={
                            <Checkbox
                              checked={
                                !!selectedEventSubscriptions[
                                  sub.JobSubscriptionId
                                ]
                              }
                              onChange={() =>
                                handleSubscriptionToggle(sub.JobSubscriptionId)
                              }
                              disabled={isConflicting || !isActive} // Disable if conflicting or inactive
                            />
                          }
                          label={
                            isActive
                              ? sub.DisplayName
                              : `${sub.DisplayName} - Ingen styring`
                          } // Show as paused if inactive
                        />
                      );
                    })}
                </Box>
              </Box>
            );
          })}

          {/* Add Event Button */}
          <DialogActions>
            <Button variant="text" size="small" onClick={handlePopoverClose}>
              Afbryd
            </Button>
            <Button variant="contained" onClick={handleAddEvent} size="small">
              Opret begivenhed
            </Button>
          </DialogActions>
        </Box>
      </Popover>

      {/* Popover for Deleting Events */}
      <Popover
        open={Boolean(deletePopoverEl)}
        anchorEl={deletePopoverEl}
        onClose={handleDeletePopoverClose}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? 'hsl(0, 0%, 100%)' : '#121212',
          }}
        >
          <ListItemText
            primary={selectedEvent.eventName}
            primaryTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
            secondary={
              selectedEvent.buildingName +
              ' - ' +
              selectedEvent.subscriptionDisplayName
            }
          />
          <IconButton
            onClick={handleDeletePopoverClose}
            sx={{
              borderRadius: 1,
              ml: 1,
              ':hover': {
                borderColor: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'hsl(0, 0%, 80%)'
                    : 'hsl(0, 0%, 30%)',
                cursor: 'pointer',
              },
            }}
          >
            <CloseRoundedIcon
              sx={{
                ':hover': { opacity: '100%', transition: 'opacity 0.3s' },
              }}
              opacity="20%"
            />
          </IconButton>
        </Box>
        <Box
          sx={{
            p: 2,
            width: '500px',
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? 'hsl(0, 0%, 100%)' : '#121212',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <EventRoundedIcon fontSize="large" sx={{ mr: 1 }} />
            <Typography component="h3" variant="h7" color="text.primary">
              {(() => {
                const startDateTime = DateTime.fromISO(
                  selectedEvent.start,
                ).setLocale('da');
                const endDateTime = DateTime.fromISO(
                  selectedEvent.end,
                ).setLocale('da');

                if (startDateTime.hasSame(endDateTime, 'day')) {
                  // Same day: show the date once with both times
                  return `${startDateTime.toFormat(
                    'd. MMMM',
                  )} ${startDateTime.toFormat(
                    'HH:mm',
                  )} - ${endDateTime.toFormat('HH:mm')}`;
                } else {
                  // Different days: show both dates and times
                  return `${startDateTime.toFormat(
                    'd. MMMM HH:mm',
                  )} - ${endDateTime.toFormat('d. MMMM HH:mm')}`;
                }
              })()}
            </Typography>
          </Box>
          <DialogActions sx={{ mt: 2 }}>
            <Button
              variant="text"
              size="small"
              onClick={handleDeletePopoverClose}
            >
              Afbryd
            </Button>
            <Button
              variant="contained"
              onClick={handleRemoveEvent}
              size="small"
              color="error"
            >
              Fjern begivenhed
            </Button>
          </DialogActions>
        </Box>
      </Popover>
    </>
  );
};

export default EventCalendar;
